import type { NavigationGroupDto } from '@an-crm/shared';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';
import { CircleUser, CornerDownLeft, House, LogOut, MoonStar, Search } from 'lucide-react';
import { type ReactNode } from 'react';
import { SoonBadge } from '@/components/ui/badge';
import { NavIcon } from '@/components/ui/nav-icon';
import { toggleTheme } from '@/lib/theme';

function PaletteItem({
  value,
  onSelect,
  icon,
  children,
}: {
  value: string;
  onSelect: () => void;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="group flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm text-fg outline-none data-[selected=true]:bg-primary-soft data-[selected=true]:text-primary-text [&_svg]:size-4"
    >
      <span className="flex text-fg-subtle group-data-[selected=true]:text-brand">{icon}</span>
      <span className="flex flex-1 items-center gap-2">{children}</span>
      <CornerDownLeft className="opacity-0 group-data-[selected=true]:opacity-60" aria-hidden />
    </Command.Item>
  );
}

const groupClass =
  '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-fg-subtle [&_[cmdk-group-heading]]:uppercase';

export function CommandPalette({
  open,
  onOpenChange,
  nav,
  onSignOut,
  supportMode = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nav: NavigationGroupDto[];
  onSignOut: () => void;
  /** in a support session "sign out" ends the session instead */
  supportMode?: boolean;
}) {
  const navigate = useNavigate();
  const run = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      overlayClassName="fixed inset-0 z-50 bg-[rgb(6_22_24/0.45)] backdrop-blur-[2px] data-[state=open]:animate-[fade-in_160ms_ease-out]"
      contentClassName="fixed top-[14vh] left-1/2 z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-e3 data-[state=open]:animate-[pop-in_200ms_var(--ease-out-soft)]"
    >
      <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
      <DialogPrimitive.Description className="sr-only">Search pages and actions</DialogPrimitive.Description>
      <div className="flex items-center gap-3 border-b border-border px-4">
        <Search className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        <Command.Input
          placeholder="Search pages and actions…"
          className="h-13 flex-1 bg-transparent text-[15px] text-fg outline-none placeholder:text-fg-subtle"
        />
        <kbd className="rounded border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-fg-subtle">
          ESC
        </kbd>
      </div>
      <Command.List className="max-h-[min(60vh,420px)] overflow-y-auto p-2">
        <Command.Empty className="px-3 py-10 text-center text-sm text-fg-muted">
          No results found.
        </Command.Empty>
        <Command.Group heading="Navigate" className={groupClass}>
          <PaletteItem
            value="home overview dashboard"
            onSelect={() => run(() => void navigate({ to: '/' }))}
            icon={<House />}
          >
            Home
          </PaletteItem>
          <PaletteItem
            value="account profile password security sessions devices"
            onSelect={() => run(() => void navigate({ to: '/account' }))}
            icon={<CircleUser />}
          >
            Account
          </PaletteItem>
        </Command.Group>
        {nav.map((group) => (
          <Command.Group key={group.group} heading={group.label} className={groupClass}>
            {group.items.map((item) => (
              <PaletteItem
                key={item.key}
                value={`${group.label} ${item.label} ${item.path}`}
                onSelect={() => run(() => void navigate({ to: item.path }))}
                icon={<NavIcon name={item.icon} />}
              >
                {item.label}
                {item.status === 'planned' && <SoonBadge />}
              </PaletteItem>
            ))}
          </Command.Group>
        ))}
        <Command.Group heading="Actions" className={groupClass}>
          <PaletteItem
            value="toggle theme dark light mode appearance"
            onSelect={() => run(toggleTheme)}
            icon={<MoonStar />}
          >
            Toggle theme
          </PaletteItem>
          <PaletteItem
            value={supportMode ? 'end support session exit' : 'sign out log out logout'}
            onSelect={() => run(onSignOut)}
            icon={<LogOut />}
          >
            {supportMode ? 'End support session' : 'Sign out'}
          </PaletteItem>
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
}
